import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { getDifference } from "app/tools/common"
import { getCompactFormattedNumber } from "app/tools/number"
import { refreshInventory } from "form/inventory/tools/common"
import {
  CREATIVE_FORECAST_VIDEO_MEDIA_NAME,
  IForm,
  IFormChange,
  IFormDataLoader,
  IFormMediaCreativeForecastRow
} from "form/model"
import { state } from "form/state"
import { formActionCreator } from "form/store/actions"
import { getFormatById } from "format/tools/common"
import {
  ICampaignMediaVideo,
  IMedia,
  IMediaCreativeForecastInfo,
  MEDIA_CONTENT_TYPES,
  MEDIA_SIMPLE_TYPES
} from "media/model"
import { mediaRPO } from "media/repository"
import {
  createCampaignMedia,
  deleteCampaignMedia,
  getMediaCreativeForecastIdFromForecast,
  getMediaCreativeForecastIdFromMedia,
  getMediaCreativeForecastIdFromVideo,
  getMediaCreativeForecastInfoFromId
} from "media/tools/common"
import { getContentTypeId } from "media/tools/media-type"
import { finalizeChange, getEmptyFormChange } from "./change"
import { getForm } from "./common"
import { getCreativeChange } from "./creative"

export const mediaCreativeForecastLoader: IFormDataLoader = async (form: IForm, onFinish: (error?: string) => any) => {
  try {
    form.context.mediaCreative.initialForecast = await mediaRPO.getMediaCreativeForecast(form.tempCampaignId)
    onFinish()
  } catch (error) {
    console.error(error)
    onFinish(`Media creative data-loader error ${String(error)}`)
  }
}

// @recursive
export function refreshMediaCreativeList(form: IForm): void {
  if (state.mediaCreativeForecastIsLoading) {
    state.mediaCreativeForecastRefreshAgain = true
    return
  } else {
    state.mediaCreativeForecastIsLoading = true
  }
  getStore().dispatch(formActionCreator.startLoadingMediaCreativeForecast(form.context.appContext))
  const finish = () => {
    state.mediaCreativeForecastIsLoading = false
    if (state.mediaCreativeForecastRefreshAgain) {
      state.mediaCreativeForecastRefreshAgain = false
      // @recursivity
      refreshMediaCreativeList(form)
    }
  }
  mediaRPO
    .getMediaCreativeForecast(form.tempCampaignId)
    .then(newForecast => {
      getStore().dispatch(formActionCreator.setMediaCreativeForecast(form.context.appContext, newForecast))
      finish()
    })
    .catch(error => {
      console.error(error)
      getStore().dispatch(formActionCreator.stopLoadingMediaCreativeForecast(form.context.appContext))
      finish()
    })
}

export function getMediaCreativeForecastRows(form: IForm): IFormMediaCreativeForecastRow[] {
  const {
    context: {
      mediaCreative: { initialForecast, currentForecast },
      isForecast
    },
    creatives: { medias, selectedRequiredMediaCreativeIds }
  } = form
  if (!currentForecast) {
    return []
  }
  const selectedIds: string[] = isForecast
    ? selectedRequiredMediaCreativeIds
    : medias.map(m => getMediaCreativeForecastIdFromMedia(m))
  return Object.keys(currentForecast).map(forecastId => {
    const forecast = currentForecast[forecastId]
    const mediaCreativeInitialForecast = initialForecast ? initialForecast[forecastId] : undefined
    const mediaCreativeCurrentForecast = currentForecast[forecastId]
    const impressionRatio = mediaCreativeCurrentForecast
      ? `${getCompactFormattedNumber(mediaCreativeCurrentForecast.impression)}/${getCompactFormattedNumber(
          mediaCreativeCurrentForecast.impressionTotal
        )}`
      : "-"
    const impressionDiff = getDifference(
      mediaCreativeInitialForecast ? mediaCreativeInitialForecast.impressionPercent : undefined,
      mediaCreativeCurrentForecast ? mediaCreativeCurrentForecast.impressionPercent : undefined
    )
    const screensRatio = mediaCreativeCurrentForecast
      ? `${getCompactFormattedNumber(mediaCreativeCurrentForecast.screen)}/${getCompactFormattedNumber(
          mediaCreativeCurrentForecast.screenTotal
        )}`
      : "-"
    const screensDiff = getDifference(
      mediaCreativeInitialForecast ? mediaCreativeInitialForecast.screenPercent : undefined,
      mediaCreativeCurrentForecast ? mediaCreativeCurrentForecast.screenPercent : undefined
    )
    return {
      ...forecast,
      impressionRatio,
      impressionDiff,
      screensRatio,
      screensDiff,
      isUploaded: selectedIds.includes(forecastId)
    }
  })
}

export function getRequiredMediaCreativeBoxTranslation(): string {
  const form = getForm()
  if (form.creatives.selectedRequiredMediaCreativeIds.length === 0) {
    return translate("label.all.means.included")
  }
  return `${form.creatives.selectedRequiredMediaCreativeIds.length} / ${
    Object.keys(form.context.mediaCreative.currentForecast).length
  }`
}

export async function synchronizeNewRequiredMediaSelection(newSelectedIds: string[]): Promise<void> {
  getStore().dispatch(formActionCreator.startSynchronizingMediaCreativeForecast(getForm().context.appContext))
  clearTimeout(state.mediaCreativeForecastTimeout)
  state.mediaCreativeForecastTimeout = setTimeout(() => setRequiredMedias(newSelectedIds), 800)
}

export function getSummaryMediaCreativeInfos(form: IForm): IMediaCreativeForecastInfo[] {
  const result: IMediaCreativeForecastInfo[] = []
  const medias = form.creatives.medias
  for (const media of medias) {
    const info = getMediaCreativeForecastInfo(media)
    if (!info) {
      continue
    }
    result.push(info)
  }
  return result
}

export function getMediaCreativeForecastInfo(media: IMedia): IMediaCreativeForecastInfo | undefined {
  if (media.name === CREATIVE_FORECAST_VIDEO_MEDIA_NAME) {
    if (media.videos.length === 0) {
      return
    }
    const video = media.videos[0]
    return {
      forecastId: getMediaCreativeForecastIdFromVideo(video),
      mediaType: MEDIA_SIMPLE_TYPES.VIDEO,
      formatId: null,
      width: video.width as number,
      height: video.height as number
    }
  } else {
    const format = getFormatById(media.format_id as number)
    if (format) {
      return {
        forecastId: getMediaCreativeForecastIdFromMedia(media),
        mediaType: MEDIA_SIMPLE_TYPES.VIDEO,
        formatId: format.id,
        width: format.width as number,
        height: format.height as number
      }
    } else {
      const width: number = media.width as number
      const height: number = media.height as number
      const isVideo = getContentTypeId(MEDIA_CONTENT_TYPES.VIDEO) === media.content_type_id
      return {
        forecastId: getMediaCreativeForecastIdFromMedia(media),
        mediaType: isVideo ? MEDIA_SIMPLE_TYPES.VIDEO : MEDIA_SIMPLE_TYPES.IMAGE,
        formatId: media.format_id as number,
        width,
        height
      }
    }
  }
}

export function getSelectedMediaCreativeId(form: IForm, medias: IMedia[]): string[] {
  if (!form.context.isForecast) {
    return []
  }
  const mediaCreatives: string[] = []
  for (const media of medias) {
    if (media.name === CREATIVE_FORECAST_VIDEO_MEDIA_NAME) {
      if (media.videos.length === 0) {
        continue
      }
    }
    mediaCreatives.push(getMediaCreativeForecastIdFromMedia(media))
  }
  return mediaCreatives
}

export function getForecastCreativeChange(form: IForm, newMedias: IMedia[]): IFormChange {
  const change: IFormChange = getEmptyFormChange("media.creatives.required")
  const previousMedias = form.context.initialData ? form.context.initialData.creatives.medias : []
  for (const newMedia of newMedias) {
    const oldMedia = previousMedias.findByProperty("id", newMedia.id)
    if (!oldMedia) {
      if (newMedia.name === CREATIVE_FORECAST_VIDEO_MEDIA_NAME) {
        change.addedItems.push(...newMedia.videos.map(v => `${newMedia.id}-video-${v.id}`))
      } else {
        change.addedItems.push(`${newMedia.id}-image-${newMedia.format_id}`)
      }
    }
  }
  for (const oldMedia of previousMedias) {
    const newMedia = newMedias.findByProperty("id", oldMedia.id)
    if (!newMedia) {
      if (oldMedia.name === CREATIVE_FORECAST_VIDEO_MEDIA_NAME) {
        change.deletedItems.push(...oldMedia.videos.map(v => `${oldMedia.id}-video-${v.id}`))
      } else {
        change.deletedItems.push(`${oldMedia.id}-image-${oldMedia.format_id}`)
      }
    }
  }
  return finalizeChange(change)
}

// @recursive
async function setRequiredMedias(newSelectedRowIds: string[]): Promise<void> {
  if (state.mediaCreativeForecastIsSynchronizing) {
    state.mediaCreativeForecastToApplySelection = newSelectedRowIds
    return
  } else {
    state.mediaCreativeForecastIsSynchronizing = true
  }
  const form = getForm()
  const newSelectedRequiredMediaCreatives = form.creatives.selectedRequiredMediaCreativeIds.slice()
  const newMedias = form.creatives.medias.slice()
  let hasError = false
  const currentlySelectedIds = form.creatives.selectedRequiredMediaCreativeIds
  const toDeleteIds = currentlySelectedIds.filter(id => newSelectedRowIds.indexOf(id) === -1)
  const toAddIds = newSelectedRowIds.filter(id => currentlySelectedIds.indexOf(id) === -1)
  const toProcessPromiseCount = toDeleteIds.length + toAddIds.length
  let processedPromiseCount = 0
  if (toProcessPromiseCount === 0) {
    state.mediaCreativeForecastIsSynchronizing = false
    getStore().dispatch(formActionCreator.stopSynchronizingMediaCreativeForecast(form.context.appContext))
    return
  }
  const onFinish = () => {
    if (toProcessPromiseCount === ++processedPromiseCount) {
      getStore().dispatch(
        formActionCreator.setCreatives(
          form.context.appContext,
          {
            medias: newMedias,
            selectedRequiredMediaCreativeIds: newSelectedRequiredMediaCreatives,
            isSynchronizing: false,
            hasSynchronizeError: hasError
          },
          getCreativeChange(form, newMedias)
        )
      )
      state.mediaCreativeForecastIsSynchronizing = false
      if (state.mediaCreativeForecastToApplySelection) {
        const toApplySelection = state.mediaCreativeForecastToApplySelection
        state.mediaCreativeForecastToApplySelection = null
        // @recursivity
        setRequiredMedias(toApplySelection)
      } else if (!hasError) {
        refreshInventory()
      }
    }
  }
  const onError = (error: any) => {
    console.error(error)
    hasError = true
    onFinish()
  }
  for (const toDeleteId of toDeleteIds) {
    const selectedId = currentlySelectedIds.find(id => id === toDeleteId)
    if (selectedId) {
      deleteForecastMedia(selectedId, newSelectedRequiredMediaCreatives, newMedias)
        .then(() => onFinish())
        .catch(error => onError(error))
    } else {
      onFinish()
    }
  }
  for (const toAddId of toAddIds) {
    const selectedId = currentlySelectedIds.find(id => id === toAddId)
    if (selectedId) {
      onFinish()
    } else {
      createForecastMedia(getMediaCreativeForecastInfoFromId(toAddId), newSelectedRequiredMediaCreatives, newMedias)
        .then(() => onFinish())
        .catch(error => onError(error))
    }
  }
}

async function createForecastMedia(
  forecastInfo: IMediaCreativeForecastInfo,
  newSelectedRequiredMediaCreatives: string[],
  newMedias: IMedia[]
): Promise<void> {
  const media =
    forecastInfo.mediaType === MEDIA_SIMPLE_TYPES.IMAGE
      ? await createImageForecastMedia(forecastInfo)
      : await createVideoForecastMedia(forecastInfo)
  newMedias.push(media)
  newSelectedRequiredMediaCreatives.push(getMediaCreativeForecastIdFromForecast(forecastInfo))
}

// a selected forecast image is a media with the correct format_id attached on it
async function createImageForecastMedia({ formatId }: IMediaCreativeForecastInfo): Promise<IMedia> {
  const form = getForm()
  return await createCampaignMedia({
    campaignId: form.tempCampaignId,
    mediaName: "forecast-image",
    formatId: formatId as number
  })
}

// a selected forecast video is a media having one video attached on it
async function createVideoForecastMedia({ height, width }: IMediaCreativeForecastInfo): Promise<IMedia> {
  const form = getForm()
  const media: IMedia = await createCampaignMedia({
    campaignId: form.tempCampaignId,
    mediaName: CREATIVE_FORECAST_VIDEO_MEDIA_NAME
  })
  const video: ICampaignMediaVideo = await mediaRPO.createMediaVideo({
    media_id: media.id,
    mime_type: "video/mp4",
    file_path: "fake/path",
    height,
    width
  })
  media.videos.push(video)
  return media
}

async function deleteForecastMedia(
  forecastId: string,
  newSelectedRequiredMediaCreatives: string[],
  newMedias: IMedia[]
): Promise<void> {
  const forecastInfo = getMediaCreativeForecastInfoFromId(forecastId)
  let mediaId: number
  if (forecastInfo.mediaType === MEDIA_SIMPLE_TYPES.IMAGE) {
    mediaId = await deleteImageForecastMedia(forecastInfo)
  } else {
    mediaId = await deleteVideoForecastMedia(forecastInfo)
  }
  newMedias.removeByProperty("id", mediaId)
  newSelectedRequiredMediaCreatives.remove(forecastId)
}

async function deleteImageForecastMedia({ formatId }: IMediaCreativeForecastInfo): Promise<number> {
  const form = getForm()
  const media = form.creatives.medias.find(m => m.format_id === formatId)
  if (!media) {
    console.warn("Should have find a media ...")
    return -1
  }
  await deleteCampaignMedia(form.tempCampaignId, media.campaignMediaId as number)
  return media.id
}

async function deleteVideoForecastMedia({ width, height }: IMediaCreativeForecastInfo): Promise<number> {
  const form = getForm()
  const videoMedias = form.creatives.medias.filter(m => m.name === CREATIVE_FORECAST_VIDEO_MEDIA_NAME)
  let media: IMedia | undefined
  let video: ICampaignMediaVideo | undefined
  for (const videoMedia of videoMedias) {
    if (videoMedia.videos.length === 0) {
      continue
    }
    const videoToCheck = videoMedia.videos[0]
    if (videoToCheck.height === height && videoToCheck.width === width) {
      media = videoMedia
      video = videoToCheck
      break
    }
  }
  if (!media) {
    console.warn("Should have find a media...")
    return -1
  }
  if (!video) {
    console.warn("Should have find the video...")
    return -1
  }
  await deleteCampaignMedia(form.tempCampaignId, media.campaignMediaId as number)
  return media.id
}
